import { useEffect, useState } from 'preact/hooks';
import { Hemicycle } from '../components/Hemicycle.tsx';
import { CredentialCards, type Credential } from '../components/Credentials.tsx';
import { MinutesPanel } from '../components/Minutes.tsx';
import { Result, Tally } from '../components/Tally.tsx';
import { Connection, ErrorBox, Pill } from '../components/ui.tsx';
import { api, ApiError } from '../lib/api.ts';
import { canRevote, EVENT_STATUS, quorumWarning, votingPill } from '../lib/labels.ts';
import { useEventScreen } from '../lib/useEventScreen.ts';
import type { EventSummary, VotingView } from '../lib/types.ts';

type Me = { user: { displayName: string; email: string }; roles: string[] };

export function Presidencia() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);
  const eventId = new URLSearchParams(location.search).get('evento');

  useEffect(() => {
    api<Me>('GET', '/api/auth/me').then(setMe).catch((e) => {
      if (e instanceof ApiError && e.status === 401) location.replace('/acceso');
      else setError((e as Error).message);
    });
  }, []);

  const logout = async () => {
    await api('POST', '/api/auth/logout').catch(() => {});
    location.assign('/acceso');
  };

  if (!me) return <div class="app"><ErrorBox error={error} />{!error && <p class="hint">Cargando…</p>}</div>;
  if (!me.roles.length) {
    return <div class="app narrow"><p class="error" role="alert">Tu cuenta no tiene un rol en esta organización.</p><button class="btn link" onClick={logout}>Salir</button></div>;
  }
  return (
    <div class="app">
      <header class="top no-print">
        <div><h1>{eventId ? 'Conducción de la sesión' : 'Sesiones'}</h1><p class="sub">{me.user.displayName} · {me.roles.join(', ')}</p></div>
        <div class="row">
          {eventId && <a href="/presidencia">Todas las sesiones</a>}
          {me.roles.includes('admin') && <a href="/admin">Administración</a>}
          <button class="btn link" onClick={logout}>Salir</button>
        </div>
      </header>
      {eventId ? <Console eventId={eventId} roles={me.roles} /> : <EventList />}
    </div>
  );
}

function EventList() {
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ events: EventSummary[] }>('GET', '/api/events').then((r) => setEvents(r.events)).catch((e: Error) => setError(e.message));
  }, []);
  return (
    <section class="panel">
      <ErrorBox error={error} />
      {events?.length === 0 && <p class="hint">Todavía no hay sesiones. Un admin las crea en <a href="/admin?tab=sesiones">Administración</a>.</p>}
      <ul class="list">
        {events?.map((e) => (
          <li key={e.id}><a href={`/presidencia?evento=${e.id}`}>{e.title}</a><Pill kind={e.status === 'in_session' ? 'live' : ''}>{EVENT_STATUS[e.status]}</Pill></li>
        ))}
      </ul>
    </section>
  );
}

function Console({ eventId, roles }: { eventId: string; roles: string[] }) {
  const { detail, state, connection, error, setError, sync } = useEventScreen(eventId);
  const [busy, setBusy] = useState(false);
  const [credentials, setCredentials] = useState<Credential[] | null>(null);
  const can = (...r: string[]) => r.some((x) => roles.includes(x));

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await sync();
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && e.code === 'stale_version') await sync();
    } finally {
      setBusy(false);
    }
  };

  if (!detail || !state) return <><ErrorBox error={error} /><p class="hint">Cargando sesión…</p></>;
  const voting = state.voting;
  const open = voting && voting.state !== 'closed';
  const pill = votingPill(voting);
  const warning = quorumWarning(voting, state.quorum.present);

  return (
    <>
      <div class="row no-print" style={{ marginBottom: '16px' }}>
        <h3 style={{ marginRight: 'auto' }}>{detail.event.title}</h3>
        <Pill kind={state.eventStatus === 'in_session' ? 'live' : ''}>{EVENT_STATUS[state.eventStatus]}</Pill>
        <Connection state={connection} />
        <a class="btn" href={`/pantalla/${eventId}`} target="_blank" rel="noopener">Pantalla pública</a>
      </div>
      <ErrorBox error={error} />
      <main class="grid" style={{ marginTop: '12px' }}>
        <section class="panel no-print" aria-label="Sesión">
          <h2>Sesión</h2>
          {can('presidencia') && (
            <div class="row">
              {state.eventStatus === 'scheduled' && (
                <button class="btn primary" disabled={busy} onClick={() => act(() => api('POST', `/api/events/${eventId}/status`, { status: 'in_session', version: state.eventVersion }))}>Abrir sesión</button>
              )}
              {state.eventStatus === 'in_session' && (
                <button class="btn" disabled={busy || Boolean(open)} onClick={() => act(() => api('POST', `/api/events/${eventId}/status`, { status: 'adjourned', version: state.eventVersion }))}>Levantar sesión</button>
              )}
            </div>
          )}
          <div class="row">
            <Pill kind={state.quorum.ok ? 'ok' : 'bad'}>{state.quorum.ok ? 'Hay quórum' : 'Sin quórum'}</Pill>
            <span class="num">Mínimo {state.quorum.need} de {state.quorum.total}. Presentes: {state.quorum.present}. Reglas {detail.rulesVersion}.</span>
          </div>

          <div class="block">
            <h2>Asuntos</h2>
            <ul class="list">
              {detail.matters.map((m) => {
                const last = m.lastVoting;
                // Se puede votar si nunca se votó o si la última ronda terminó en empate a repetir o sin quórum.
                const decided = last && (last.state !== 'closed' || !canRevote(last.status));
                const pill = last ? votingPill({ state: last.state, result: last.status ? { status: last.status } as VotingView['result'] : null }) : null;
                return (
                  <li key={m.id}>
                    <span>{m.title}{last && last.round > 1 ? ` (ronda ${last.round})` : ''}</span>
                    {decided && pill ? <Pill kind={pill.kind}>{pill.text}</Pill> : can('presidencia') && (
                      <button class="btn primary" disabled={busy || Boolean(open) || state.eventStatus !== 'in_session'}
                        onClick={() => act(() => api('POST', `/api/events/${eventId}/votings`, { matterId: m.id }))}>
                        {last ? 'Repetir votación' : 'Abrir votación'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
            {detail.matters.length === 0 && <p class="hint">Sin asuntos todavía.</p>}
            {can('admin', 'secretaria') && state.eventStatus !== 'adjourned' && (
              <AddMatter eventId={eventId} ruleSetId={detail.event.ruleSetId} busy={busy}
                onAdd={(body) => act(() => api('POST', `/api/events/${eventId}/matters`, body))} />
            )}
          </div>

          {can('presidencia', 'secretaria') && (
            <div class="block">
              <h2>Asistencia</h2>
              <ul class="list">
                {state.seats.map((s) => (
                  <li key={s.memberId}>
                    <label class="check">
                      <input type="checkbox" checked={s.present} disabled={busy}
                        onChange={(e) => act(() => api('PUT', `/api/events/${eventId}/attendance/${s.memberId}`, { present: e.currentTarget.checked }))} />
                      {s.displayName}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <MinutesPanel eventId={eventId} canRequest={can('admin', 'presidencia', 'secretaria')} />

          {can('admin', 'secretaria') && (
            <div class="block">
              <h2>Credenciales</h2>
              <p class="hint">Emitir de nuevo invalida los PINs y QR anteriores de esta sesión.</p>
              <button class="btn" disabled={busy}
                onClick={() => act(async () => setCredentials((await api<{ credentials: Credential[] }>('POST', `/api/events/${eventId}/credentials`, {})).credentials))}>
                Emitir PINs y QR
              </button>
            </div>
          )}
        </section>

        <section class="panel" aria-label="Votación">
          <div class="row"><Pill kind={pill.kind}>{pill.text}</Pill>{voting && <span class="num">{voting.matter.title} · {voting.mode} · reglas {voting.rulesVersion}</span>}</div>
          <Hemicycle seats={state.seats} votes={state.votes} voting={voting} present={state.quorum.present} />
          <Tally voting={voting} present={state.quorum.present} />
          {warning && <p class={warning.kind} role={warning.kind === 'error' ? 'alert' : 'status'}>{warning.text}</p>}
          {voting?.state === 'closed' && <Result voting={voting} />}
          {can('presidencia') && voting?.state === 'voting' && (
            <button class="btn primary no-print" disabled={busy} onClick={() => act(() => api('POST', `/api/votings/${voting.id}/close`, { version: voting.version }))}>Cerrar votación</button>
          )}
          {can('presidencia') && voting?.state === 'closing' && (
            <div class="block no-print">
              <p>Empate. Emite el voto de calidad:</p>
              <div class="row">
                <button class="btn vote for" disabled={busy} onClick={() => act(() => api('POST', `/api/votings/${voting.id}/casting`, { version: voting.version, choice: 'for' }))}><span>A favor</span></button>
                <button class="btn vote against" disabled={busy} onClick={() => act(() => api('POST', `/api/votings/${voting.id}/casting`, { version: voting.version, choice: 'against' }))}><span>En contra</span></button>
              </div>
            </div>
          )}
        </section>
      </main>

      {credentials && <CredentialCards credentials={credentials} eventTitle={detail.event.title} onClose={() => setCredentials(null)} />}
    </>
  );
}

/** Agregar un asunto con uno de los tipos de las reglas vigentes de la sesión. */
function AddMatter({ eventId, ruleSetId, busy, onAdd }: {
  eventId: string; ruleSetId: string; busy: boolean; onAdd: (body: { matterType: string; title: string }) => Promise<void>;
}) {
  const [types, setTypes] = useState<Array<{ id: string; label: string }>>([]);
  const [matterType, setMatterType] = useState('');
  const [title, setTitle] = useState('');
  useEffect(() => {
    api<{ ruleSet: { matters: Array<{ id: string; label: string }> } }>('GET', `/api/rule-sets/${ruleSetId}`).then((r) => {
      setTypes(r.ruleSet.matters);
      setMatterType(r.ruleSet.matters[0]?.id ?? '');
    }).catch(() => setTypes([]));
  }, [ruleSetId]);
  return (
    <form class="field" onSubmit={(e) => { e.preventDefault(); void onAdd({ matterType, title }).then(() => setTitle('')); }}>
      <label for={`asunto-${eventId}`}>Nuevo asunto</label>
      <input id={`asunto-${eventId}`} type="text" placeholder="Título" value={title} onInput={(e) => setTitle(e.currentTarget.value)} required maxLength={200} />
      <div class="row">
        <select aria-label="Tipo de asunto (define la mayoría)" value={matterType} onChange={(e) => setMatterType(e.currentTarget.value)} style={{ flex: '1 1 160px' }}>
          {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <button class="btn" type="submit" disabled={busy || !title.trim() || !matterType}>Agregar</button>
      </div>
    </form>
  );
}
